/**
 * @file Lab-side `Footer` — the copyright line at the bottom of every
 *       chromed lab screen (except the full-bleed reader). Exported
 *       with the legacy `Footer` name so lab page code keeps importing
 *       it by that name.
 * @module @labcraft/lab/ui/components/site-chrome
 *
 * Ported off the shared `@labcraft/ui` `BottomNav` when the page chrome
 * went per-app (#946): lab rendered it with empty `items`, so it was
 * only ever the brand + year copyright line. The top chrome already
 * moved to the lab-specific {@link LabAppBar}; the standalone
 * `/authorize` route still serves direct sign-in visits.
 */

import { type JSX } from 'react'
import { BRAND, LAUNCHED } from '@labcraft/ui'
import styles from './site_chrome.module.css'

/**
 * Lab-side footer — mirrors www's copyright bar (copyright on the left,
 * `BRAND.domain` on the right), the bottom strip of the www footer. The
 * rich part of www's footer (brand blurb, social icons, Site/Connect
 * sitemap) is deliberately omitted: lab is the authenticated learner
 * surface, so marketing nav (manifesto, contact) belongs to www, not
 * here. Exported as `Footer` for backwards compatibility with lab page
 * imports.
 *
 * @returns The footer element.
 */
export function Footer(): JSX.Element {
  const year = new Date().getFullYear()
  const prefix = LAUNCHED ? 'LabCraft' : 'LabCraft · Beta'
  return (
    <footer className={styles.footer}>
      <span>
        {prefix} · {year}
      </span>
      <span>{BRAND.domain}</span>
    </footer>
  )
}
